import React from 'react';
import { connect } from 'react-redux';
import { DefaultLayout } from 'src/layouts';
import { showModal, closeModal, showLoginModal, showSignupModal } from 'src/modules/shared/modal/actions';
import { setAlert } from 'src/typescript/components/alert/actions';
import { trackEvent } from 'src/utils/analytics';
import { trackFootfallEvent, trackFootfallEventWithHrefDelay } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import WithSession from 'src/components/hoc/withSession';
import Section from 'src/modules/home/components/section';
import Carousel from 'src/modules/home/components/carousel';
import FeatureList from 'src/modules/home/components/featureList';
import PressList from 'src/modules/home/components/pressList';
import DiscoverSection from 'src/modules/home/components/discoverSection';
import UserQuotesSection from 'src/modules/home/components/userQuotesSection';
import SearchSection from 'src/modules/home/components/searchSection';
import Last30DaysSection from 'src/modules/home/components/last30daysSection';
import LazyImage from 'src/utils/lazyImage';
import LazyIframe from 'src/utils/lazyIframe';
import { fetchNumbers } from 'src/modules/home/actions';
import { i18n } from 'config/i18n';
import config from 'config/config';
import { getFunctionalityCookiesConsent } from 'src/utils/cookie';

const isServer = typeof window === 'undefined';

class IndexPage extends React.Component {
  static async getInitialProps({ store: { dispatch } }) {
    await dispatch(fetchNumbers());
    return {};
  }

  componentDidMount() {
    trackEvent('Section', 'Home page');
  }

  onSubmitEmail = (e, refName) => {
    e.preventDefault();
    trackFootfallEvent(footfallEventTypes.CLICKED, {
      clickMetadata: { section: 'join' },
      clickType: 'visitor-homepage',
    });
    this.props.showSignupModal({}, null, false, this[refName].value);
  };

  render() {
    const seo = {
      canonical: 'https://healthunlocked.com',
      ogTitle: 'HealthUnlocked | The social network for health',
      description: 'We are the world’s largest online support network.\nAsk questions. Get answers.\nTalk to others who’ve been there before.\n\n#UnlockYourHealth',
      image: `${config.cdn.static}img/home/HUsocialimage.png`,
    };

    // For the Youtube video (functionality cookies), we read the 'CookieControl' cookie value here,
    // because getFunctionalityCookiesConsent() in withSession isn't called soon enough & the user might
    // still see the fallback image instead of a player if they previously accepted functionality cookies
    if (!isServer && window.isFunctionalityEnabled === undefined) {
      getFunctionalityCookiesConsent();
    }

    return (
      <DefaultLayout
        fullWidth={true}
        seo={seo}
        siteContainerClassName="site-container home"
      >
        <div className="home__background">
          <div className="home__header">
            <div className="home__banner">
              <div className="home__background-content">
                <p className="home__mobile-text">HealthUnlocked</p>
                <h1>{i18n.t('The world’s largest social network for health')}</h1>
                <p>{i18n.t('Join today. For free, forever.')}</p>
                <form className="home__form" onSubmit={e => this.onSubmitEmail(e, 'emailRef')}>
                  <input
                    aria-label="Please enter your email address"
                    className="form-control home__form-input home__form-input--small"
                    placeholder={i18n.t('email@address.com')}
                    ref={node => (this.emailRef = node)}
                    type="text"
                  />
                  <button className="btn btn-primary btn-lg" type="submit">{i18n.t('Join')}</button>
                </form>
                <p>
                  {i18n.t('Already a member?')}{' '}
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      this.props.showLoginModal();
                    }}
                  >
                    {i18n.t('Log in')}
                  </a>
                </p>
              </div>
            </div>
          </div>
          <Carousel />
        </div>

        <Section>
          <PressList />
        </Section>
        <SearchSection />
        <Section>
          <h2>{i18n.t('A holistic approach to healthcare')}</h2>
          <p>
            {i18n.t('Get information, support and tools that have been shown to improve health outcomes.')}{' '}
            <a
              href="https://about.healthunlocked.com/validation"
              rel="noopener"
              target="_blank"
              onClick={e => {
                e.preventDefault();
                trackFootfallEventWithHrefDelay('https://about.healthunlocked.com/validation', footfallEventTypes.CLICKED, {
                  clickMetadata: { section: 'see-research' },
                  clickType: 'visitor-homepage',
                });
              }}
            >
              {i18n.t('Learn about our research')} <span className="icon icon-right-open" />
            </a>
          </p>
          <FeatureList />
        </Section>
        <UserQuotesSection />
        <Section className="home__partners">
          <h2>{i18n.t('Our partners')}</h2>
          <p>
            {i18n.t(
              "Over 600 organizations have used our technology to build health communities. It's the best available and it's free to nonprofits, health advocates, and patient organizations.",
            )}{' '}
            <a
              href="/start"
              rel="noopener"
              target="_blank"
              onClick={e => {
                e.preventDefault();
                trackFootfallEventWithHrefDelay('/start', footfallEventTypes.CLICKED, {
                  clickMetadata: { section: 'partners-learnmore' },
                  clickType: 'visitor-homepage',
                });
              }}
            >
              {i18n.t('Start a community')} <span className="icon icon-right-open" />
            </a>
          </p>
          <div>
            <LazyImage
              alt="Partner logos"
              imageType="picture"
              mobileImgSrc={`${config.cdn.static}img/home/partner-logos-mobile.png`}
              src={`${config.cdn.static}img/home/partner-logos.png`}
            />
          </div>
        </Section>
        <Section className="home__video">
          { !isServer && window.isFunctionalityEnabled
            ?
              <div className="youtubeVideoContainer">
                <LazyIframe
                  attributes={{
                    allowFullScreen: true,
                    frameBorder: 0,
                    title: 'HealthUnlocked Video',
                  }}
                  src="https://www.youtube.com/embed/_MtwFuii6fM?controls=0&cc_load_policy=1&loop=1&version=3&playlist=_MtwFuii6fM&rel=0"
                />
              </div>
            :
              <a
                aria-label="Video HealthUnlocked The social network for health on Youtube"
                href="https://www.youtube.com/watch?v=_MtwFuii6fM"
                rel="noopener"
                target="_blank"
              >
                <LazyImage
                  alt="Video HealthUnlocked The social network for health on Youtube"
                  imageType="picture"
                  mobileImgSrc={`${config.cdn.static}img/home/homepage-video-fallback-img-mobile.jpg`}
                  src={`${config.cdn.static}img/home/homepage-video-fallback-img.jpg`}
                />
              </a>
          }
        </Section>
        <DiscoverSection />
        <Last30DaysSection />
        <section className="home__section-join">
          <LazyImage
            backgroundImageSrc={`${config.cdn.static}img/home/homepage4-2098x1400.jpg`}
            classnames="home__section-join-background-img"
            imageType="background"
          />
          <div className="home__section-join-overlay">
            <div className="home__section-join-content home__section-join-content--split">
            <h2 className="home__input-title">{i18n.t('Join today. For free, forever.')}</h2>
              <form className="home__form" onSubmit={e => this.onSubmitEmail(e, 'emailJoinRef')}>
                <input
                  aria-label="Please enter your email address"
                  className="form-control home__form-input"
                  placeholder={i18n.t('email@address.com')}
                  ref={node => (this.emailJoinRef = node)}
                  type="text"
                />
                <button className="btn btn-primary btn-lg" type="submit">{i18n.t('Join')}</button>
              </form>
            </div>
          </div>
        </section>
      </DefaultLayout>
    );
  }
}

const mapStateToProps = state => {
  return {
    currentUser: state.context.currentUser,
  };
};

const actions = {
  showModal,
  closeModal,
  setAlert,
  showSignupModal,
  showLoginModal,
};

export default connect(mapStateToProps, actions)(WithSession(IndexPage));
